/**
 * MandatesPanel — shared by Brand CampaignProgressTab and CM BulkCampaignWorkspace.
 *
 * Renders the mandate card grid, the "Add Mandate" modal, and the inline
 * edit/delete affordances. The data source is identical (the new
 * /api/brand/campaigns/{id}/mandates endpoints) so brand + CM see the same rows.
 *
 * Props
 *   campaignId      — brand campaign id
 *   mandates        — current mandate list (parent fetches once + passes down)
 *   onChange        — async () => void; called after create/edit/delete to refetch
 *   roster          — [{id, name}] assignable creator rows (bulk_creator row_ids
 *                     for bulk campaigns, child campaign ids for exclusive)
 *   readOnly        — defaults to false. Hides Add/Edit/Delete affordances.
 *
 * No business logic beyond CRUD + light validation.
 */
import React, { useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { toast } from 'sonner';
import {
  FileText, ScrollText, Shirt, ImageIcon, Hash, MoreHorizontal, Plus, Pencil, Trash2,
  Paperclip, Users, UserCheck, X, AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MANDATE_TYPES = [
  { value: 'script',        label: 'Script',        Icon: ScrollText },
  { value: 'dos_and_donts', label: "Do's & Don'ts", Icon: AlertCircle },
  { value: 'outfit',        label: 'Outfit',        Icon: Shirt },
  { value: 'background',    label: 'Background',    Icon: ImageIcon },
  { value: 'hashtags',      label: 'Hashtags',      Icon: Hash },
  { value: 'other',         label: 'Other',         Icon: MoreHorizontal },
];

const TYPE_META = Object.fromEntries(MANDATE_TYPES.map((t) => [t.value, t]));

const TYPE_COLOR = {
  script:        '#FF451A',
  dos_and_donts: '#FFB800',
  outfit:        '#A855F7',
  background:    '#06B6D4',
  hashtags:      '#84CC16',
  other:         '#71717a',
};

const EMPTY = {
  type: 'script',
  title: '',
  content: '',
  assigned_to: 'all',
  selectedIds: [],
  file: null,
};

export const MandatesPanel = ({ campaignId, mandates, onChange, roster = [], readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null); // mandate object or null
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit   = (m) => {
    setEditing(m);
    setForm({
      type: m.type,
      title: m.title || '',
      content: m.content || '',
      assigned_to: m.assigned_to === 'all' ? 'all' : 'specific',
      selectedIds: Array.isArray(m.assigned_to) ? m.assigned_to : [],
      file: null,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        // PATCH JSON
        const payload = {
          type: form.type,
          title: form.title,
          content: form.content,
          assigned_to: form.assigned_to === 'all' ? 'all' : form.selectedIds,
        };
        await axios.patch(
          `${API_URL}/api/brand/campaigns/${campaignId}/mandates/${editing.mandate_id}`,
          payload,
          { withCredentials: true },
        );
        toast.success('Mandate updated');
      } else {
        // POST multipart (so an optional file can ride along)
        const fd = new FormData();
        fd.append('type', form.type);
        fd.append('title', form.title);
        fd.append('content', form.content);
        fd.append('assigned_to', form.assigned_to === 'all' ? 'all' : JSON.stringify(form.selectedIds));
        if (form.file) fd.append('file', form.file);
        await axios.post(
          `${API_URL}/api/brand/campaigns/${campaignId}/mandates`,
          fd,
          { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } },
        );
        toast.success('Mandate added');
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save mandate');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete mandate "${m.title}"?`)) return;
    try {
      await axios.delete(
        `${API_URL}/api/brand/campaigns/${campaignId}/mandates/${m.mandate_id}`,
        { withCredentials: true },
      );
      toast.success('Mandate deleted');
      await onChange?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <section className="space-y-3" data-testid="mandates-panel">
      <header className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-mono uppercase tracking-[0.2em] text-zinc-300 flex items-center gap-2">
            <FileText size={14} className="text-[#FF451A]" /> Mandates
          </h3>
          <p className="text-[11px] text-[#6B6B6B] dark:text-zinc-500 mt-0.5">Instructions that go to creators alongside the brief.</p>
        </div>
        {!readOnly && (
          <Button
            onClick={openCreate}
            className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white rounded-full px-4 py-2 text-xs"
            data-testid="mandate-add-btn"
          >
            <Plus size={13} className="mr-1.5" /> Add Mandate
          </Button>
        )}
      </header>

      {(mandates || []).length === 0 ? (
        <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-6 text-center text-sm text-[#6B6B6B] dark:text-zinc-500" data-testid="mandates-empty">
          No mandates yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
          {(mandates || []).map((m) => {
            const meta = TYPE_META[m.type] || TYPE_META.other;
            const color = TYPE_COLOR[m.type] || '#71717a';
            const assignedAll = m.assigned_to === 'all';
            const assignedList = Array.isArray(m.assigned_to) ? m.assigned_to : [];
            return (
              <div key={m.mandate_id} className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-3" data-testid="mandate-card">
                <div className="flex items-start gap-3">
                  <span
                    className="px-2 py-1 rounded-md text-[10px] font-mono uppercase tracking-wider shrink-0 flex items-center gap-1"
                    style={{ backgroundColor: `${color}15`, color }}
                  >
                    <meta.Icon size={10} /> {meta.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-[#0F0F0F] dark:text-white truncate" data-testid="mandate-title">{m.title}</div>
                    {m.content && (
                      <p className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-1 whitespace-pre-wrap break-words">{m.content}</p>
                    )}
                    <div className="flex items-center gap-2 mt-2 text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
                      {assignedAll ? (
                        <span className="inline-flex items-center gap-1"><Users size={10} /> All creators</span>
                      ) : (
                        <span className="inline-flex items-center gap-1"><UserCheck size={10} /> {assignedList.length} creator{assignedList.length === 1 ? '' : 's'}</span>
                      )}
                      <span>·</span>
                      <span>by {m.added_by_name} ({m.added_by_role})</span>
                      <span>·</span>
                      <span>{String(m.created_at || '').slice(0, 10)}</span>
                    </div>
                    {m.file_url && (
                      <a
                        href={m.file_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-[#FF451A] hover:text-white mt-2"
                        data-testid="mandate-file-link"
                      >
                        <Paperclip size={11} /> {m.file_name || 'Download attachment'}
                      </a>
                    )}
                  </div>
                  {!readOnly && (
                    <div className="flex gap-1 shrink-0">
                      <button
                        onClick={() => openEdit(m)}
                        className="p-1.5 rounded hover:bg-white/5 text-[#6B6B6B] dark:text-zinc-400 hover:text-white"
                        data-testid="mandate-edit-btn"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => remove(m)}
                        className="p-1.5 rounded hover:bg-[#FF3B30]/10 text-[#6B6B6B] dark:text-zinc-400 hover:text-[#FF3B30]"
                        data-testid="mandate-delete-btn"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!saving && !o) { setOpen(false); setEditing(null); } }}>
        <DialogContent className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 text-[#0F0F0F] dark:text-white max-w-lg" data-testid="mandate-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <FileText size={18} className="text-[#FF451A]" /> {editing ? 'Edit Mandate' : 'New Mandate'}
            </DialogTitle>
            <DialogDescription className="text-xs text-[#6B6B6B] dark:text-zinc-500">
              {editing ? 'Update the instruction.' : 'Add an instruction visible to brand + CM (creators never see this UI).'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider block mb-1.5 text-[#0F0F0F] dark:text-white">Type</label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MANDATE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider block mb-1.5 text-[#0F0F0F] dark:text-white">Title</label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Opening hook line"
                required
                className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="mandate-title-input"
              />
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider block mb-1.5 text-[#0F0F0F] dark:text-white">Content</label>
              <textarea
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={4}
                placeholder="Detailed instruction…"
                className="w-full rounded-md text-sm px-3 py-2 font-body resize-y bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="mandate-content-input"
              />
            </div>

            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider block mb-1.5 text-[#0F0F0F] dark:text-white">Assign to</label>
              <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                <SelectTrigger className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500" data-testid="mandate-assigned-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All creators on this campaign</SelectItem>
                  <SelectItem value="specific">Specific creators</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {form.assigned_to === 'specific' && (
              <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.10] dark:border-white/10 rounded-md p-2 max-h-40 overflow-auto" data-testid="mandate-specific-list">
                {roster.length === 0 ? (
                  <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 italic">No creators assigned to this campaign yet.</p>
                ) : roster.map((c) => {
                  const checked = (form.selectedIds || []).includes(c.id);
                  return (
                    <label key={c.id} className="flex items-center gap-2 py-1 px-1 hover:bg-white/5 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = checked
                            ? form.selectedIds.filter((x) => x !== c.id)
                            : [...(form.selectedIds || []), c.id];
                          setForm({ ...form, selectedIds: next });
                        }}
                        className="accent-[#FF451A]"
                        data-testid={`mandate-creator-checkbox-${c.id}`}
                      />
                      <span className="text-xs text-[#0F0F0F] dark:text-white">{c.name}</span>
                    </label>
                  );
                })}
              </div>
            )}

            {!editing && (
              <div>
                <label className="text-[10px] font-mono uppercase tracking-wider block mb-1.5 text-[#0F0F0F] dark:text-white">Optional file</label>
                <input
                  type="file"
                  onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                  className="text-xs text-zinc-300"
                  data-testid="mandate-file-input"
                />
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={saving} className="bg-[#FF451A] hover:bg-[#FF451A]/90 text-[#0F0F0F] dark:text-white rounded-full px-5" data-testid="mandate-save-btn">
                {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Add Mandate')}
              </Button>
              <Button
                type="button"
                onClick={() => { setOpen(false); setEditing(null); }}
                disabled={saving}
                className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5"
              >
                <X size={14} className="mr-1" /> Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// Compact mandate tag — used on creator rows.
export const MandateTag = ({ mandate, onClick }) => {
  const meta = TYPE_META[mandate.type] || TYPE_META.other;
  const color = TYPE_COLOR[mandate.type] || '#71717a';
  return (
    <button
      type="button"
      onClick={onClick}
      className="px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider inline-flex items-center gap-1 shrink-0 hover:opacity-80"
      style={{ backgroundColor: `${color}15`, color }}
      title={mandate.title}
      data-testid="mandate-tag"
    >
      <meta.Icon size={8} /> {mandate.title.length > 18 ? `${mandate.title.slice(0, 18)}…` : mandate.title}
    </button>
  );
};

// Returns mandates that apply to a given creator row id (`all` or row_id present in the assigned_to list).
export const mandatesForCreator = (mandates, rowId) =>
  (mandates || []).filter((m) =>
    m.assigned_to === 'all' || (Array.isArray(m.assigned_to) && m.assigned_to.includes(rowId))
  );
